import React from "react";
import {
  Box,
  Pagination,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react";
import {
  getSwayyCustomerContact,
  getSwayyCustomerName,
  SwayyOrder,
} from "../../../../../models/SwayyOrder/SwayyOrder";
import { getSwayyStatusTone } from "../../../../../models/SwayyOrder/SwayyOrderFilters";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import { CurrencyHelper } from "../../../../../utils/CurrencyHelper";
import { useStores } from "../../../use-stores";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import SkuCopyRow from "../../../../../views/components/common/SkuCopyRow";
import { SwayyTableEmptyState } from "../SwayyPageShell";
import {
  swayyTableBodyCellLeftSx,
  swayyTableBodyCellSx,
  swayyTableHeadCellLeftSx,
  swayyTableHeadCellSx,
  swayyTableHeadRowSx,
  swayyTableSortLabelSx,
} from "../swayyTableStyles";

const getSwayyOrderItemCount = (order: SwayyOrder): number =>
  order.items?.reduce((sum, item) => sum + (item.qty ?? 0), 0) ??
  order.items?.length ??
  0;

const PAGE_SIZE_OPTIONS: FilterValue[] = [
  { id: "10", value: "10 / page" },
  { id: "20", value: "20 / page" },
  { id: "50", value: "50 / page" },
  { id: "100", value: "100 / page" },
];

interface SwayyOrdersTableProps {
  onViewOrder: (order: SwayyOrder) => void;
  onRetry?: () => void;
}

const thSx = swayyTableHeadCellSx;
const thLeftSx = swayyTableHeadCellLeftSx;
const sortLabelSx = swayyTableSortLabelSx;
const tdSx = swayyTableBodyCellSx;
const tdLeftSx = swayyTableBodyCellLeftSx;

const StatusBadge = ({
  label,
  tone,
}: {
  label: string;
  tone: "pine" | "success" | "warning" | "danger" | "muted";
}) => {
  const styles: Record<string, { bg: string; color: string }> = {
    pine: { bg: swayyColors.pineLight, color: swayyColors.deepGreen },
    success: { bg: "rgba(30, 166, 114, 0.12)", color: swayyColors.success },
    warning: { bg: "rgba(217, 119, 6, 0.12)", color: swayyColors.warning },
    danger: { bg: "rgba(220, 38, 38, 0.1)", color: swayyColors.danger },
    muted: { bg: swayyColors.pageBg, color: swayyColors.textSecondary },
  };
  const toneStyle = styles[tone];

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        px: 1.25,
        py: 0.35,
        borderRadius: swayyRadii.full,
        bgcolor: toneStyle.bg,
        color: toneStyle.color,
        fontSize: 11,
        fontWeight: 700,
        fontFamily: swayyFont,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
      }}
    >
      {label.replace(/_/g, " ")}
    </Box>
  );
};

const LoadingSkeleton = ({ isMobile }: { isMobile: boolean }) => {
  if (isMobile) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: 2 }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Paper
            key={i}
            elevation={0}
            sx={{
              p: 2,
              borderRadius: swayyRadii.lg,
              border: `1px solid ${swayyColors.border}`,
            }}
          >
            <Skeleton width="40%" height={22} />
            <Skeleton width="60%" height={18} sx={{ mt: 1 }} />
            <Skeleton width="50%" height={18} sx={{ mt: 1 }} />
            <Skeleton width="30%" height={24} sx={{ mt: 1.5 }} />
          </Paper>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            gap: 2,
            px: 2,
            py: 1.75,
            borderBottom: `1px solid ${swayyColors.border}`,
          }}
        >
          {Array.from({ length: 6 }).map((__, j) => (
            <Skeleton key={j} variant="text" width="14%" height={20} />
          ))}
        </Box>
      ))}
    </Box>
  );
};

const OrderMobileCard = ({
  order,
  onViewOrder,
}: {
  order: SwayyOrder;
  onViewOrder: (order: SwayyOrder) => void;
}) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: swayyRadii.lg,
      border: `1px solid ${swayyColors.border}`,
      bgcolor: swayyColors.white,
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 1,
        mb: 1.5,
      }}
    >
      <Box>
        <Box
          onClick={() => onViewOrder(order)}
          sx={{ cursor: "pointer", maxWidth: "100%" }}
        >
          <SkuCopyRow
            sku={order.order_number}
            emptyFallback="—"
            copyLabel="Copy order id"
            color={swayyColors.deepGreen}
            fontWeight={700}
            typographySx={{
              fontFamily: swayyFont,
              fontSize: 14,
              textDecoration: "underline",
              textUnderlineOffset: "3px",
              "&:hover": { color: swayyColors.pine },
            }}
          />
        </Box>
        <CustomTypography
          variant="body3"
          color={swayyColors.textSecondary}
          sx={{ fontFamily: swayyFont, mt: 0.25 }}
        >
          {formatDate(order.placed_at)}
        </CustomTypography>
      </Box>
      <StatusBadge
        label={order.status || "—"}
        tone={getSwayyStatusTone(order.status || "")}
      />
    </Box>

    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <MobileField
        label="Customer"
        value={getSwayyCustomerName(order.customer)}
        sub={getSwayyCustomerContact(order.customer)}
      />
      <MobileField label="Channel" value={order.channel || "—"} />
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <StatusBadge
          label={order.payment_status || "—"}
          tone={getSwayyStatusTone(order.payment_status || "")}
        />
        <StatusBadge
          label={order.payment_method || "—"}
          tone={getSwayyStatusTone(order.payment_method || "")}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 0.5,
          pt: 1,
          borderTop: `1px solid ${swayyColors.border}`,
        }}
      >
        <CustomTypography
          variant="body3"
          color={swayyColors.textSecondary}
          sx={{ fontFamily: swayyFont }}
        >
          {getSwayyOrderItemCount(order)} items
        </CustomTypography>
        <CustomTypography
          variant="body2"
          color={swayyColors.deepGreen}
          sx={{ fontFamily: swayyFont, fontWeight: 600 }}
        >
          {CurrencyHelper.formatCurrency(
            order.grand_total ?? 0,
            order.currency,
          )}
        </CustomTypography>
      </Box>
    </Box>
  </Paper>
);

const MobileField = ({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) => (
  <Box>
    <CustomTypography
      variant="body3"
      color={swayyColors.textSecondary}
      sx={{ fontFamily: swayyFont, fontSize: 11, mb: 0.15 }}
    >
      {label}
    </CustomTypography>
    <CustomTypography
      variant="body2"
      color={swayyColors.deepGreen}
      sx={{ fontFamily: swayyFont, fontWeight: 500 }}
    >
      {value}
    </CustomTypography>
    {sub && (
      <CustomTypography
        variant="body3"
        color={swayyColors.textSecondary}
        sx={{ fontFamily: swayyFont, fontSize: 11 }}
      >
        {sub}
      </CustomTypography>
    )}
  </Box>
);

const SwayyOrdersTable: React.FC<SwayyOrdersTableProps> = ({
  onViewOrder,
  onRetry,
}) => {
  const { swayyOrderStore: ordersStore } = useStores();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { total, page, limit } = {
    total: ordersStore.pagination.total,
    page: ordersStore.params.page,
    limit: ordersStore.params.limit,
  };
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const pageCount = Math.max(1, Math.ceil(total / limit) || 1);

  const paginationFooter = (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderTop: `1px solid ${swayyColors.border}`,
      }}
    >
      <CustomTypography
        variant="body3"
        color={swayyColors.textSecondary}
        sx={{ fontFamily: swayyFont, fontSize: 12 }}
      >
        Showing {from}–{to} of {total} orders
      </CustomTypography>

      <Pagination
        count={pageCount}
        page={page}
        onChange={(_, next) => ordersStore.handlePageChange(next)}
        shape="rounded"
        size="small"
        sx={{
          "& .MuiPaginationItem-root": {
            fontFamily: swayyFont,
            color: swayyColors.deepGreen,
            borderRadius: swayyRadii.sm,
          },
          "& .Mui-selected": {
            bgcolor: `${swayyColors.pine} !important`,
            color: `${swayyColors.white} !important`,
            "&:hover": { bgcolor: `${swayyColors.pineHover} !important` },
          },
        }}
      />

      <Box sx={{ minWidth: 120 }}>
        <AppSearchDropdownMulti
          title="Rows"
          isMultiSelect={false}
          list={PAGE_SIZE_OPTIONS}
          value={PAGE_SIZE_OPTIONS.filter(
            (option) => option.id === String(limit),
          )}
          local={true}
          fontSize="12px"
          minWidth="120px"
          handleSelect={(items: FilterValue[]) =>
            ordersStore.handleRowsPerPageChange(Number(items?.[0]?.id) || 20)
          }
        />
      </Box>
    </Box>
  );

  if (ordersStore.loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: swayyRadii.lg,
          border: `1px solid ${swayyColors.border}`,
          overflow: "hidden",
          bgcolor: swayyColors.white,
        }}
      >
        <LoadingSkeleton isMobile={isMobile} />
      </Paper>
    );
  }

  if (ordersStore.error && ordersStore.orders.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: swayyRadii.lg,
          border: `1px solid ${swayyColors.border}`,
          overflow: "hidden",
          bgcolor: swayyColors.white,
        }}
      >
        <SwayyTableEmptyState
          title="Couldn't load orders"
          description={ordersStore.error}
          actionLabel="Try again"
          onAction={onRetry}
          danger
        />
      </Paper>
    );
  }

  if (isMobile) {
    return (
      <Box>
        {ordersStore.orders.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: swayyRadii.lg,
              border: `1px solid ${swayyColors.border}`,
              bgcolor: swayyColors.white,
            }}
          >
            <SwayyTableEmptyState
              title="No orders found"
              description="No orders match the current filters. Try adjusting search or filters."
              actionLabel="Clear filters"
              onAction={() => ordersStore.clearFilters()}
            />
          </Paper>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {ordersStore.orders.map((order) => (
              <OrderMobileCard
                key={order.id}
                order={order}
                onViewOrder={onViewOrder}
              />
            ))}
            <Paper
              elevation={0}
              sx={{
                borderRadius: swayyRadii.lg,
                border: `1px solid ${swayyColors.border}`,
                bgcolor: swayyColors.white,
                overflow: "hidden",
              }}
            >
              {paginationFooter}
            </Paper>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: swayyRadii.lg,
        border: `1px solid ${swayyColors.border}`,
        overflow: "hidden",
        bgcolor: swayyColors.white,
      }}
    >
      <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow sx={swayyTableHeadRowSx}>
              <TableCell sx={thLeftSx}>
                <TableSortLabel
                  active={ordersStore.params.sort_by === "order_number"}
                  direction={
                    ordersStore.params.sort_by === "order_number"
                      ? ordersStore.params.sort_order || "asc"
                      : "asc"
                  }
                  onClick={() => ordersStore.setSort("order_number")}
                  sx={sortLabelSx}
                >
                  Order
                </TableSortLabel>
              </TableCell>
              <TableCell sx={thSx}>Customer</TableCell>
              <TableCell sx={thSx}>Channel</TableCell>
              <TableCell sx={thSx}>Status</TableCell>
              <TableCell sx={thSx}>Payment Status</TableCell>
              <TableCell sx={thSx}>Payment Method</TableCell>
              <TableCell sx={thSx}>Items</TableCell>
              <TableCell sx={thSx}>Total</TableCell>
              <TableCell sx={{ ...thSx, minWidth: 170, width: 170 }}>
                <TableSortLabel
                  active={ordersStore.params.sort_by === "placed_at"}
                  direction={
                    ordersStore.params.sort_by === "placed_at"
                      ? ordersStore.params.sort_order || "desc"
                      : "desc"
                  }
                  onClick={() => ordersStore.setSort("placed_at")}
                  sx={sortLabelSx}
                >
                  Placed At
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ordersStore.orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>
                  <SwayyTableEmptyState
                    title="No orders found"
                    description="No orders match the current filters. Try adjusting search or filters."
                    actionLabel="Clear filters"
                    onAction={() => ordersStore.clearFilters()}
                  />
                </TableCell>
              </TableRow>
            ) : (
              ordersStore.orders.map((order) => (
                <TableRow
                  key={order.id}
                  hover
                  sx={{
                    "&:hover": { bgcolor: swayyColors.pineLight },
                  }}
                >
                  <TableCell sx={tdLeftSx}>
                    <Box
                      onClick={() => onViewOrder(order)}
                      sx={{ cursor: "pointer", maxWidth: "100%" }}
                    >
                      <SkuCopyRow
                        sku={order.order_number}
                        emptyFallback="—"
                        copyLabel="Copy order id"
                        color={swayyColors.deepGreen}
                        fontWeight={700}
                        typographySx={{
                          fontFamily: swayyFont,
                          fontSize: 13,
                          textDecoration: "underline",
                          textUnderlineOffset: "3px",
                          "&:hover": { color: swayyColors.pine },
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: swayyColors.deepGreen,
                        fontFamily: swayyFont,
                      }}
                    >
                      {getSwayyCustomerName(order.customer)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: swayyColors.textSecondary,
                        fontFamily: swayyFont,
                      }}
                    >
                      {getSwayyCustomerContact(order.customer)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <StatusBadge label={order.channel || "—"} tone="pine" />
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <StatusBadge
                      label={order.status || "—"}
                      tone={getSwayyStatusTone(order.status || "")}
                    />
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <StatusBadge
                      label={order.payment_status || "—"}
                      tone={getSwayyStatusTone(order.payment_status || "")}
                    />
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <StatusBadge
                      label={order.payment_method || "—"}
                      tone={getSwayyStatusTone(order.payment_method || "")}
                    />
                  </TableCell>
                  <TableCell sx={tdSx}>
                    {getSwayyOrderItemCount(order)}
                  </TableCell>
                  <TableCell sx={{ ...tdSx, fontWeight: 500 }}>
                    {CurrencyHelper.formatCurrency(
                      order.grand_total ?? 0,
                      order.currency,
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tdSx,
                      minWidth: 170,
                      width: 170,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatDate(order.placed_at)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {ordersStore.orders.length > 0 && paginationFooter}
    </Paper>
  );
};

export default observer(SwayyOrdersTable);
